import JSZip from "jszip";
import ExcelJS from "exceljs";

export interface CsvHeader {
  key: string;
  label: string;
}

export interface BackupItemResult {
  pdf: { blob: Blob; fileName: string } | null;
  row: Record<string, unknown>;
  warning?: string;
}

export interface BulkBackupOptions<T> {
  items: T[];
  zipRootName: string;
  concurrency?: number;
  getLabel: (item: T) => string;
  processItem: (item: T) => Promise<BackupItemResult>;
  csvHeaders: CsvHeader[];
  csvXlsxBaseName: string;
  onProgress?: (done: number, total: number) => void;
}

export interface BulkBackupSummary {
  blob: Blob;
  zipFileName: string;
  totalItems: number;
  pdfCount: number;
  skippedCount: number;
  failedItems: { label: string; reason: string }[];
  partialWarnings: { label: string; reason: string }[];
}

export interface PdfOnlyBackupItemResult {
  pdf: { blob: Blob; fileName: string } | null;
  folderPath?: string[];
  warning?: string;
}

export interface PdfOnlyBackupOptions<T> {
  items: T[];
  zipRootName: string;
  concurrency?: number;
  getLabel: (item: T) => string;
  processItem: (item: T) => Promise<PdfOnlyBackupItemResult>;
  onProgress?: (done: number, total: number) => void;
}

const DIACRITICS_PATTERN = /\p{Diacritic}/gu;
const ZIP_SEGMENT_SANITIZE_PATTERN = /[\\/:*?"<>|]+/g;

export function sanitizeFileName(text: string): string {
  const withoutAccents = text.normalize("NFD").replace(DIACRITICS_PATTERN, "");
  return withoutAccents
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase();
}

export function sanitizeZipPathSegment(text: string): string {
  const withoutAccents = String(text ?? "")
    .normalize("NFD")
    .replace(DIACRITICS_PATTERN, "");

  const cleaned = withoutAccents
    .split("")
    .filter((character) => {
      const charCode = character.charCodeAt(0);
      return charCode >= 32 && charCode !== 127;
    })
    .join("")
    .replace(ZIP_SEGMENT_SANITIZE_PATTERN, " ")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned || "Sem nome";
}

export function formatDocumentDateForFileName(dateLike: Date | string | null | undefined): string {
  if (!dateLike) {
    return "sem_data";
  }

  const parsedDate = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(parsedDate.getTime())) {
    return "sem_data";
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizePdfText(text: unknown): string {
  return String(text ?? "")
    .normalize("NFD")
    .replace(DIACRITICS_PATTERN, "")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function escapeCsvValue(value: unknown): string {
  const str = value === null || value === undefined ? "" : String(value);
  if (/[",;\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function buildCsv(rows: Record<string, unknown>[], headers: CsvHeader[]): string {
  const headerLine = headers.map((header) => escapeCsvValue(header.label)).join(";");
  const lines = rows.map((row) =>
    headers.map((header) => escapeCsvValue(row[header.key])).join(";")
  );
  return [headerLine, ...lines].join("\r\n");
}

export async function buildXlsxBuffer(
  rows: Record<string, unknown>[],
  headers: CsvHeader[]
): Promise<ArrayBuffer> {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Relatório");
  sheet.columns = headers.map((header) => ({ header: header.label, key: header.key, width: 24 }));
  rows.forEach((row) => sheet.addRow(row));
  sheet.getRow(1).font = { bold: true };
  const buffer = await workbook.xlsx.writeBuffer();
  return buffer;
}

async function mapWithConcurrency<T>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<void>
): Promise<void> {
  let cursor = 0;

  async function runNext(): Promise<void> {
    const currentIndex = cursor;
    cursor += 1;
    if (currentIndex >= items.length) return;
    await worker(items[currentIndex]);
    await runNext();
  }

  const workerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workerCount }, () => runNext()));
}

function resolveZipFolder(root: JSZip | null, folderPath?: string[]): JSZip | null {
  if (!root) {
    return null;
  }

  if (!folderPath || folderPath.length === 0) {
    return root;
  }

  return folderPath.reduce<JSZip | null>((currentFolder, segment) => {
    if (!currentFolder) {
      return null;
    }

    return currentFolder.folder(sanitizeZipPathSegment(segment));
  }, root);
}

/**
 * Orquestra a geração de um backup completo (.zip com PDF/CSV/XLSX) a partir de uma
 * lista de itens já carregada na tela — reaproveitado por ConfigDocumentEmployeePage e
 * ConfigReportDocument para evitar duplicar a lógica de zip/CSV/XLSX em cada página.
 */
export async function runBulkBackup<T>(options: BulkBackupOptions<T>): Promise<BulkBackupSummary> {
  const {
    items,
    zipRootName,
    concurrency = 3,
    getLabel,
    processItem,
    csvHeaders,
    csvXlsxBaseName,
    onProgress,
  } = options;

  const zip = new JSZip();
  const root = zip.folder(zipRootName);
  const pdfFolder = root!.folder("PDF");
  const csvFolder = root!.folder("CSV");
  const xlsxFolder = root!.folder("XLSX");

  const rows: Record<string, unknown>[] = [];
  const failedItems: { label: string; reason: string }[] = [];
  const partialWarnings: { label: string; reason: string }[] = [];
  let pdfCount = 0;
  let skippedCount = 0;
  let done = 0;

  await mapWithConcurrency(items, concurrency, async (item) => {
    const label = getLabel(item);
    try {
      const result = await processItem(item);

      if (result.pdf) {
        pdfFolder!.file(result.pdf.fileName, result.pdf.blob);
        pdfCount += 1;
      } else {
        skippedCount += 1;
      }

      rows.push(result.row);

      if (result.warning) {
        partialWarnings.push({ label, reason: result.warning });
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : "Erro desconhecido";
      failedItems.push({ label, reason });
    } finally {
      done += 1;
      onProgress?.(done, items.length);
    }
  });

  const csvContent = buildCsv(rows, csvHeaders);
  const xlsxBuffer = await buildXlsxBuffer(rows, csvHeaders);

  csvFolder!.file(`${csvXlsxBaseName}.csv`, csvContent);
  xlsxFolder!.file(`${csvXlsxBaseName}.xlsx`, xlsxBuffer);

  const blob = await zip.generateAsync({ type: "blob" });

  return {
    blob,
    zipFileName: `${zipRootName}.zip`,
    totalItems: items.length,
    pdfCount,
    skippedCount,
    failedItems,
    partialWarnings,
  };
}

export async function runPdfOnlyBackup<T>(
  options: PdfOnlyBackupOptions<T>
): Promise<BulkBackupSummary> {
  const {
    items,
    zipRootName,
    concurrency = 3,
    getLabel,
    processItem,
    onProgress,
  } = options;

  const zip = new JSZip();
  const root = zip.folder(zipRootName);
  const failedItems: { label: string; reason: string }[] = [];
  const partialWarnings: { label: string; reason: string }[] = [];
  let pdfCount = 0;
  let skippedCount = 0;
  let done = 0;

  await mapWithConcurrency(items, concurrency, async (item) => {
    const label = getLabel(item);
    try {
      const result = await processItem(item);
      const targetFolder = resolveZipFolder(root, result.folderPath);

      if (result.pdf) {
        targetFolder!.file(result.pdf.fileName, result.pdf.blob);
        pdfCount += 1;
      } else {
        resolveZipFolder(root, result.folderPath);
        skippedCount += 1;
      }

      if (result.warning) {
        partialWarnings.push({ label, reason: result.warning });
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : "Erro desconhecido";
      failedItems.push({ label, reason });
    } finally {
      done += 1;
      onProgress?.(done, items.length);
    }
  });

  const blob = await zip.generateAsync({ type: "blob" });

  return {
    blob,
    zipFileName: `${zipRootName}.zip`,
    totalItems: items.length,
    pdfCount,
    skippedCount,
    failedItems,
    partialWarnings,
  };
}

export function formatDateForFileName(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function createSimplePdfBlob(title: string, lines: string[]): Blob {
  const safeTitle = normalizePdfText(title);
  const safeLines = lines.map((line) => normalizePdfText(line));

  const content = [
    "BT",
    "/F1 18 Tf",
    "50 790 Td",
    `(${safeTitle}) Tj`,
    "0 -30 Td",
    "/F1 11 Tf",
    "16 TL",
    ...safeLines.flatMap((line) => [`(${line}) Tj`, "T*"]),
    "ET",
  ].join("\n");

  const contentBytes = new TextEncoder().encode(content);
  const encoder = new TextEncoder();
  const objects = [
    "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
    "2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj",
    "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >> endobj",
    "4 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj",
    `5 0 obj << /Length ${contentBytes.length} >> stream\n${content}\nendstream endobj`,
  ];

  let pdf = "%PDF-1.4\n";
  const offsets: number[] = [0];

  objects.forEach((object) => {
    offsets.push(encoder.encode(pdf).length);
    pdf += `${object}\n`;
  });

  const xrefOffset = encoder.encode(pdf).length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\n`;
  pdf += `startxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
}
